/**
 * CommanderNight Component
 * 
 * Auto-recurring Wednesday Commander Night signup system with
 * anti-abuse rules, pod management, and strike tracking.
 * Integrated into the Events tab.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Swords, Users, Clock, Shield, AlertTriangle, CheckCircle,
    X, Loader2, ChevronDown, ChevronUp, ExternalLink, Crown,
    UserPlus, UserMinus, Ban, Eye
} from 'lucide-react';
import { supabase, authService } from '../lib/supabase';

// ============================================
// TYPES
// ============================================

type SignupStatus = 'registered' | 'confirmed' | 'dropped' | 'no_show' | 'played';
type SessionPhase = 'registration' | 'pods_assigned' | 'confirmation' | 'locked' | 'live' | 'completed';

interface CommanderNightSession {
    id: string;
    event_date: string; // The Wednesday date
    phase: SessionPhase;
    registration_opens: string;
    registration_closes: string;
    confirmation_deadline: string;
    lock_date: string;
    max_pods: number;
    created_at: string;
}

interface CommanderNightSignup {
    id: string;
    session_id: string;
    user_id: string;
    player_name: string;
    deck_name?: string;
    deck_id?: string;
    commander_name?: string;
    color_identity?: string[];
    status: SignupStatus;
    pod_number: number | null;
    pod_seat: number | null;
    waitlist_position: number | null;
    signed_up_at: string;
    confirmed_at: string | null;
    dropped_at: string | null;
    drop_reason: string | null;
}

interface PlayerStrike {
    id: string;
    user_id: string;
    event_date: string;
    strike_type: 'no_show' | 'late_withdraw';
    issued_at: string;
    expires_at: string;
}

interface Pod {
    number: number;
    players: CommanderNightSignup[];
    isFull: boolean;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function getNextWednesday(): Date {
    const now = new Date();
    const day = now.getDay();
    const daysUntilWed = day <= 3 ? 3 - day : 10 - day;
    const nextWed = new Date(now);
    nextWed.setDate(now.getDate() + daysUntilWed);
    nextWed.setHours(17, 30, 0, 0); // 5:30 PM
    return nextWed;
}

function getUpcomingSession(): { eventDate: Date; regOpens: Date; regCloses: Date; confirmDeadline: Date; lockDate: Date } {
    const eventDate = getNextWednesday();
    const regOpens = new Date(eventDate);
    regOpens.setDate(eventDate.getDate() - 14); // 2 weeks before
    const regCloses = new Date(eventDate);
    regCloses.setDate(eventDate.getDate() - 7); // 1 week before
    regCloses.setHours(23, 59, 59, 0);
    const confirmDeadline = new Date(eventDate);
    confirmDeadline.setDate(eventDate.getDate() - 2); // Monday before
    const lockDate = new Date(eventDate);
    lockDate.setDate(eventDate.getDate() - 1); // Tuesday before

    return { eventDate, regOpens, regCloses, confirmDeadline, lockDate };
}

function formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function getTimeUntil(target: Date): string {
    const now = new Date();
    const diff = target.getTime() - now.getTime();
    if (diff <= 0) return 'Now';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h`;
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
}

function getCurrentPhase(session: { regOpens: Date; regCloses: Date; confirmDeadline: Date; lockDate: Date; eventDate: Date }): SessionPhase {
    const now = new Date();
    if (now < session.regOpens) return 'registration'; // Pre-registration
    if (now < session.regCloses) return 'registration';
    if (now < session.confirmDeadline) return 'confirmation';
    if (now < session.lockDate) return 'locked';
    if (now < new Date(session.eventDate.getTime() + 3 * 60 * 60 * 1000)) return 'live'; // Event + 3hrs
    return 'completed';
}

const PHASE_CONFIG: Record<SessionPhase, { label: string; color: string; bgColor: string; description: string }> = {
    registration: { label: 'Registration Open', color: 'text-green-400', bgColor: 'bg-green-500/20', description: 'Sign up for a pod!' },
    pods_assigned: { label: 'Pods Assigned', color: 'text-blue-400', bgColor: 'bg-blue-500/20', description: 'Check your pod assignment' },
    confirmation: { label: 'Confirm Your Spot', color: 'text-amber-400', bgColor: 'bg-amber-500/20', description: 'Confirm you\'re attending' },
    locked: { label: 'Pods Locked', color: 'text-purple-400', bgColor: 'bg-purple-500/20', description: 'Final lineup set' },
    live: { label: '🔴 LIVE', color: 'text-red-400', bgColor: 'bg-red-500/20', description: 'Commander Night is happening!' },
    completed: { label: 'Completed', color: 'text-white/40', bgColor: 'bg-white/10', description: 'See you next week!' },
};

const COLOR_PIPS: Record<string, string> = {
    W: '⚪', U: '🔵', B: '⚫', R: '🔴', G: '🟢', C: '◇'
};

// ============================================
// MAIN COMPONENT
// ============================================

interface CommanderNightProps {
    className?: string;
}

export function CommanderNight({ className = '' }: CommanderNightProps) {
    // Auth
    const [user, setUser] = useState<{ id: string; email: string } | null>(null);
    const [userName, setUserName] = useState('');

    // Data
    const [signups, setSignups] = useState<CommanderNightSignup[]>([]);
    const [mySignup, setMySignup] = useState<CommanderNightSignup | null>(null);
    const [myStrikes, setMyStrikes] = useState<PlayerStrike[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // UI
    const [expanded, setExpanded] = useState(true);
    const [showPods, setShowPods] = useState(false);
    const [showRules, setShowRules] = useState(false);

    // Session info
    const sessionDates = useMemo(() => getUpcomingSession(), []);
    const currentPhase = useMemo(() => getCurrentPhase(sessionDates), [sessionDates]);
    const phaseConfig = PHASE_CONFIG[currentPhase];

    // Check auth
    useEffect(() => {
        const checkAuth = async () => {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
                setUser({ id: currentUser.id, email: currentUser.email || '' });
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('display_name')
                    .eq('id', currentUser.id)
                    .single();
                setUserName(profile?.display_name || currentUser.email?.split('@')[0] || 'Player');
            }
        };
        checkAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setUser({ id: session.user.id, email: session.user.email || '' });
            } else {
                setUser(null);
                setMySignup(null);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Fetch signups
    const fetchSignups = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const eventDateStr = sessionDates.eventDate.toISOString().split('T')[0];

            const { data, error: fetchError } = await supabase
                .from('commander_night_signups')
                .select('*')
                .eq('event_date', eventDateStr)
                .order('signed_up_at', { ascending: true });

            if (fetchError) {
                const msg = (fetchError.message || '').toLowerCase();
                const code = fetchError.code || '';
                if (code === '42P01' || msg.includes('does not exist') || msg.includes('relation') || msg.includes('not found') || code === 'PGRST204') {
                    // Table doesn't exist yet — show empty state gracefully
                    setSignups([]);
                    setLoading(false);
                    return;
                }
                throw fetchError;
            }

            setSignups(data || []);

            // Find my signup
            if (user) {
                const mine = (data || []).find(s => s.user_id === user.id);
                setMySignup(mine || null);
            }
        } catch (err: unknown) {
            console.error('Failed to fetch commander night signups:', err);
            setError('Failed to load signups');
        } finally {
            setLoading(false);
        }
    }, [sessionDates.eventDate, user]);

    // Fetch my strikes
    const fetchStrikes = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await supabase
                .from('commander_night_strikes')
                .select('*')
                .eq('user_id', user.id)
                .gte('expires_at', new Date().toISOString());

            setMyStrikes(data || []);
        } catch {
            // Strikes table may not exist yet
            setMyStrikes([]);
        }
    }, [user]);

    useEffect(() => { fetchSignups(); }, [fetchSignups]);
    useEffect(() => { fetchStrikes(); }, [fetchStrikes]);

    // Sign up
    const handleSignUp = async () => {
        if (!user) return;
        setActionLoading(true);
        setError(null);

        try {
            // Check for active ban
            const { data: bans } = await supabase
                .from('commander_night_bans')
                .select('*')
                .eq('user_id', user.id)
                .gte('banned_until', new Date().toISOString());

            if (bans && bans.length > 0) {
                setError(`You are banned from Commander Night until ${new Date(bans[0].banned_until).toLocaleDateString()}`);
                setActionLoading(false);
                return;
            }

            const eventDateStr = sessionDates.eventDate.toISOString().split('T')[0];

            const { data, error: insertError } = await supabase
                .from('commander_night_signups')
                .insert({
                    event_date: eventDateStr,
                    user_id: user.id,
                    player_name: userName,
                    status: 'registered',
                })
                .select()
                .single();

            if (insertError) throw insertError;
            if (data) {
                setMySignup(data);
                setSignups(prev => [...prev, data]);
            }
        } catch (err: unknown) {
            console.error('Failed to sign up:', err);
            setError(err instanceof Error ? err.message : 'Failed to sign up');
        } finally {
            setActionLoading(false);
        }
    };

    // Confirm spot
    const handleConfirm = async () => {
        if (!user || !mySignup) return;
        setActionLoading(true);

        try {
            const { error: updateError } = await supabase
                .from('commander_night_signups')
                .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
                .eq('id', mySignup.id);

            if (updateError) throw updateError;
            setMySignup({ ...mySignup, status: 'confirmed', confirmed_at: new Date().toISOString() });
            setSignups(prev => prev.map(s => s.id === mySignup.id ? { ...s, status: 'confirmed' as SignupStatus } : s));
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to confirm');
        } finally {
            setActionLoading(false);
        }
    };

    // Withdraw
    const handleWithdraw = async () => {
        if (!user || !mySignup) return;
        if (!confirm('Are you sure you want to withdraw from Commander Night?')) return;
        setActionLoading(true);

        try {
            const { error: updateError } = await supabase
                .from('commander_night_signups')
                .update({
                    status: 'dropped',
                    dropped_at: new Date().toISOString(),
                    drop_reason: 'voluntary',
                })
                .eq('id', mySignup.id);

            if (updateError) throw updateError;
            setMySignup(null);
            setSignups(prev => prev.filter(s => s.id !== mySignup.id));
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to withdraw');
        } finally {
            setActionLoading(false);
        }
    };

    // Build pods from signups
    const pods = useMemo((): Pod[] => {
        const active = signups.filter(s => s.status !== 'dropped' && s.status !== 'no_show');
        const podMap = new Map<number, CommanderNightSignup[]>();

        // Group by pod number
        active.forEach(s => {
            if (s.pod_number !== null) {
                const existing = podMap.get(s.pod_number) || [];
                existing.push(s);
                podMap.set(s.pod_number, existing);
            }
        });

        // If no pods assigned yet, estimate
        if (podMap.size === 0 && active.length > 0) {
            const podCount = Math.ceil(active.length / 4);
            for (let i = 0; i < podCount; i++) {
                const podPlayers = active.slice(i * 4, (i + 1) * 4);
                podMap.set(i + 1, podPlayers);
            }
        }

        return Array.from(podMap.entries())
            .sort(([a], [b]) => a - b)
            .map(([num, players]) => ({
                number: num,
                players,
                isFull: players.length >= 4,
            }));
    }, [signups]);

    const activeSignups = signups.filter(s => s.status !== 'dropped' && s.status !== 'no_show');
    const confirmedCount = signups.filter(s => s.status === 'confirmed').length;
    // Allow sign-ups from registration through confirmation (until pods lock)
    const canSignUp = ['registration', 'confirmation'].includes(currentPhase) && !mySignup && user && myStrikes.length < 3;
    // Allow confirming from confirmation through locked (until event goes live)
    const canConfirm = ['confirmation', 'locked'].includes(currentPhase) && mySignup?.status === 'registered';
    const canWithdraw = mySignup && ['registered', 'confirmed'].includes(mySignup.status) && currentPhase !== 'live';
    const isBanned = myStrikes.length >= 3;

    return (
        <div className={`rounded-xl border overflow-hidden ${className} ${currentPhase === 'live'
            ? 'border-red-500/40 bg-gradient-to-br from-red-900/20 via-[#0f172a] to-purple-900/20'
            : 'border-purple-500/30 bg-gradient-to-br from-purple-900/20 via-[#0f172a] to-amber-900/10'
            }`}>
            {/* Header */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Swords className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className="text-left">
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold text-white">Commander Night</h2>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${phaseConfig.bgColor} ${phaseConfig.color}`}>
                                {phaseConfig.label}
                            </span>
                        </div>
                        <p className="text-sm text-white/50">
                            Every Wednesday • 5:30 PM EST • {formatDate(sessionDates.eventDate)}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {/* Player count */}
                    <div className="text-right hidden sm:block">
                        <div className="text-sm font-medium text-white">{activeSignups.length} players</div>
                        <div className="text-xs text-white/40">{pods.length} pods forming</div>
                    </div>
                    {/* Twitch badge */}
                    <a
                        href="https://twitch.tv/deckstrgg"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="hidden sm:flex items-center gap-1 px-2 py-1 bg-purple-600/30 rounded text-xs text-purple-300 hover:bg-purple-600/50 transition-colors"
                    >
                        📺 twitch.tv/deckstrgg
                    </a>
                    {expanded ? <ChevronUp className="size-4 text-white/40" /> : <ChevronDown className="size-4 text-white/40" />}
                </div>
            </button>

            {/* Expandable content */}
            <div className={`transition-all duration-300 overflow-hidden ${expanded ? 'max-h-[800px]' : 'max-h-0'}`}>
                <div className="px-4 pb-4 space-y-4">
                    {/* Timeline */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <TimelineStep
                            label="Registration"
                            date={`Closes ${formatDate(sessionDates.regCloses)}`}
                            active={currentPhase === 'registration'}
                            completed={['confirmation', 'locked', 'live', 'completed'].includes(currentPhase)}
                            countdown={currentPhase === 'registration' ? getTimeUntil(sessionDates.regCloses) : undefined}
                        />
                        <TimelineStep
                            label="Confirm Spot"
                            date={`By ${formatDate(sessionDates.confirmDeadline)}`}
                            active={currentPhase === 'confirmation'}
                            completed={['locked', 'live', 'completed'].includes(currentPhase)}
                            countdown={currentPhase === 'confirmation' ? getTimeUntil(sessionDates.confirmDeadline) : undefined}
                        />
                        <TimelineStep
                            label="Pods Locked"
                            date={formatDate(sessionDates.lockDate)}
                            active={currentPhase === 'locked'}
                            completed={['live', 'completed'].includes(currentPhase)}
                        />
                        <TimelineStep
                            label="Event Night"
                            date={`${formatDate(sessionDates.eventDate)} ${formatTime(sessionDates.eventDate)}`}
                            active={currentPhase === 'live'}
                            completed={currentPhase === 'completed'}
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {error}
                            <button onClick={() => setError(null)} className="ml-auto">
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    )}

                    {/* Strike Warning */}
                    {myStrikes.length > 0 && (
                        <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-300 text-sm flex items-center gap-2">
                            <Shield className="w-4 h-4 flex-shrink-0" />
                            You have {myStrikes.length}/3 active strikes.
                            {isBanned ? ' You are currently banned from Commander Night.' : ' 3 strikes = 30-day ban.'}
                        </div>
                    )}

                    {/* Action Area */}
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Sign Up */}
                        {canSignUp && (
                            <button
                                onClick={handleSignUp}
                                disabled={actionLoading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
                            >
                                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                Sign Up for Commander Night
                            </button>
                        )}

                        {/* Not logged in */}
                        {!user && ['registration', 'confirmation'].includes(currentPhase) && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white/50 rounded-lg text-sm">
                                Sign in to register for Commander Night
                            </div>
                        )}

                        {/* Already signed up */}
                        {mySignup && mySignup.status === 'registered' && ['registration', 'confirmation'].includes(currentPhase) && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 text-green-300 rounded-lg text-sm">
                                <CheckCircle className="w-4 h-4" />
                                You're signed up! Pod assignments after registration closes.
                            </div>
                        )}

                        {/* Confirm button */}
                        {canConfirm && (
                            <button
                                onClick={handleConfirm}
                                disabled={actionLoading}
                                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium animate-pulse"
                            >
                                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                Confirm Your Spot
                            </button>
                        )}

                        {/* Confirmed status */}
                        {mySignup?.status === 'confirmed' && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 text-green-300 rounded-lg text-sm">
                                <CheckCircle className="w-4 h-4" />
                                Confirmed! {mySignup.pod_number ? `Pod ${mySignup.pod_number}` : 'Pod assignment coming soon'}
                            </div>
                        )}

                        {/* Withdraw */}
                        {canWithdraw && (
                            <button
                                onClick={handleWithdraw}
                                disabled={actionLoading}
                                className="flex items-center gap-1 px-3 py-1.5 text-red-400/70 hover:text-red-400 text-xs transition-colors"
                            >
                                <UserMinus className="w-3 h-3" />
                                Withdraw
                            </button>
                        )}

                        {/* Banned */}
                        {isBanned && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-300 rounded-lg text-sm">
                                <Ban className="w-4 h-4" />
                                Banned from Commander Night (too many no-shows)
                            </div>
                        )}

                        {/* Right side controls */}
                        <div className="ml-auto flex items-center gap-2">
                            <button
                                onClick={() => setShowPods(!showPods)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-xs rounded-lg transition-colors"
                            >
                                <Eye className="w-3 h-3" />
                                {showPods ? 'Hide' : 'View'} Pods ({pods.length})
                            </button>
                            <button
                                onClick={() => setShowRules(!showRules)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-xs rounded-lg transition-colors"
                            >
                                <Shield className="w-3 h-3" />
                                Rules
                            </button>
                        </div>
                    </div>

                    {/* Pods Display */}
                    {showPods && (
                        <div className="space-y-2">
                            <h3 className="text-sm font-medium text-white/60 flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Pods ({pods.length})
                            </h3>
                            {loading ? (
                                <div className="flex justify-center py-4">
                                    <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                                </div>
                            ) : pods.length === 0 ? (
                                <div className="text-center py-4 text-white/30 text-sm">
                                    No pods yet — sign up to start forming pods!
                                </div>
                            ) : (
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {pods.map(pod => (
                                        <div
                                            key={pod.number}
                                            className={`p-3 rounded-lg border ${pod.isFull
                                                ? 'bg-green-500/10 border-green-500/20'
                                                : 'bg-white/5 border-white/10'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-white">Pod {pod.number}</span>
                                                <span className="text-xs text-white/40">{pod.players.length}/4</span>
                                            </div>
                                            <div className="space-y-1">
                                                {pod.players.map(p => (
                                                    <div key={p.id} className="flex items-center justify-between text-xs">
                                                        <span className={`${p.user_id === user?.id ? 'text-purple-300 font-medium' : 'text-white/60'}`}>
                                                            {p.player_name}
                                                        </span>
                                                        <div className="flex items-center gap-1">
                                                            {p.commander_name && (
                                                                <span className="text-white/30 truncate max-w-[100px]">{p.commander_name}</span>
                                                            )}
                                                            {p.color_identity?.map(c => (
                                                                <span key={c} className="text-[10px]">{COLOR_PIPS[c] || c}</span>
                                                            ))}
                                                            {p.status === 'confirmed' && (
                                                                <CheckCircle className="w-3 h-3 text-green-400" />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                {Array.from({ length: 4 - pod.players.length }).map((_, i) => (
                                                    <div key={`empty-${i}`} className="text-xs text-white/20 italic">
                                                        Open slot
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Rules */}
                    {showRules && (
                        <div className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
                            <h3 className="text-sm font-medium text-white flex items-center gap-2">
                                <Shield className="w-4 h-4 text-purple-400" />
                                Commander Night Rules
                            </h3>
                            <div className="space-y-2 text-xs text-white/60">
                                <div className="flex gap-2">
                                    <span className="text-green-400">✓</span>
                                    <span>Registration opens 2 weeks before, closes 1 week before</span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-green-400">✓</span>
                                    <span>Pods are assigned after registration closes — must confirm within 4 days</span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-green-400">✓</span>
                                    <span>Withdraw before registration closes = no penalty</span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-amber-400">⚠</span>
                                    <span>Withdraw after pod assignment = warning (tracked)</span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-red-400">✗</span>
                                    <span>No-show on event night = 1 strike</span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-red-400">✗</span>
                                    <span>3 strikes in 60 days = banned for 30 days</span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-purple-400">♦</span>
                                    <span>Strikes expire after 60 days of good behavior</span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-purple-400">♦</span>
                                    <span>Players with 0 strikes get priority in pod assignments</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SpellTable link */}
                    {currentPhase === 'live' && (
                        <a
                            href="https://spelltable.wizards.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 p-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-300 transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Open SpellTable
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================
// TIMELINE STEP SUB-COMPONENT
// ============================================

function TimelineStep({ label, date, active, completed, countdown }: {
    label: string;
    date: string;
    active: boolean;
    completed: boolean;
    countdown?: string;
}) {
    return (
        <div className={`p-2 rounded-lg text-center transition-colors ${active ? 'bg-purple-500/20 border border-purple-500/30' :
            completed ? 'bg-green-500/10 border border-green-500/20' :
                'bg-white/5 border border-white/5'
            }`}>
            <div className={`text-xs font-medium ${active ? 'text-purple-300' : completed ? 'text-green-400' : 'text-white/40'}`}>
                {completed && <CheckCircle className="w-3 h-3 inline mr-1" />}
                {label}
            </div>
            <div className="text-[10px] text-white/30 mt-0.5">{date}</div>
            {countdown && (
                <div className="text-[10px] text-amber-400 mt-0.5 font-mono">{countdown}</div>
            )}
        </div>
    );
}

export default CommanderNight;
